import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useT } from "../i18n";
import { useTelefon } from "./geraet";

/* ══════════════════════════════════════════════════════════════════
   Die zweite Bahn der Seitenleiste.

   Die Einstellungen haben eine eigene Navigation. Bis zum 16.09.2026
   stand sie als **zweite Leiste** in der Maske, und die erste wurde
   dafür ausgeblendet: Beim Hineingehen verschwand die eine, beim
   Herausgehen kam sie zurück. Beides ohne Übergang, beide 224 px breit
   — es sah aus, als würde die Leiste blinzeln.

   Luca, 16.09.2026: *„Wenn ich in die Einstellungen gehe, soll die
   Seitenleiste sauber rübersliden und das zurück zur Arbeit … es muss
   weiter in der Seitenleiste sein, weil das intuitiver ist."*

   Jetzt ist es **dieselbe** Leiste mit zwei Bahnen nebeneinander, die
   in ihr verschoben werden (`stile/schale.css`, „Die Leiste schiebt
   um"). Was in die zweite Bahn gehört, weiß aber die Maske, nicht die
   Schale: Welches Blatt gerade offen ist, steht in den Einstellungen.

   Darum derselbe Weg wie beim Maskenkopf (`Kopf.tsx`): Die Maske
   schreibt ihre Navigation da hin, wo sie inhaltlich hingehört, und ein
   Portal stellt sie dorthin, wo sie hingehört. Kein Zustand, der
   zwischen Schale und Maske hin und her gereicht wird.
   ══════════════════════════════════════════════════════════════════ */

type Raum = { ziel: HTMLElement | null; beiWahl: () => void };

const Ziel = createContext<Raum>({ ziel: null, beiWahl: () => {} });

export function NebenleisteRaum({ ziel, beiWahl, children }: Raum & { children: ReactNode }) {
  return <Ziel.Provider value={{ ziel, beiWahl }}>{children}</Ziel.Provider>;
}

/* ══════════════ Am Telefon: die Liste ist ein eigener Schirm (17.09.2026) ══════════════

   Am Schreibtisch steht die zweite Bahn neben dem Blatt, und man wechselt
   mit einem Klick. Am Telefon lag sie hinter dem Menüknopf in der
   Schublade — **zwei Griffe je Blattwechsel, und ohne sie zu öffnen sah
   man nicht, dass es zwölf Blätter gibt.** Die Einstellungen öffneten auf
   „Firma und Logo", und nichts auf dem Schirm sagte, dass daneben noch
   Benutzer, Nummernkreise und Lexware stehen. Und die Schublade selbst
   soll am Telefon wegfallen (40-mobiles-grundgeruest.md, Schritt 1).

   Also dieselbe Ordnung wie jede Liste am Telefon: **erst die Liste, dann
   das Blatt, und im Blatt der Rückweg zur Liste.** Die Maske sagt mit
   `schirm`, ob gerade die Liste dran ist; am Schreibtisch hat das keine
   Wirkung. Was sie hineinschreibt, bleibt dasselbe — Gruppen und Punkte
   wie in der Seitenleiste. */

/** Was hier hineingeschrieben wird, erscheint in der zweiten Bahn der Leiste — am Telefon als eigener Schirm. */
export function Nebenleiste({ children, schirm = false, schild }: {
  children: ReactNode;
  /** Am Telefon: Steht gerade die Liste da (statt eines Blatts)? */
  schirm?: boolean;
  /** i18n-Schlüssel für das `aria-label` der Liste am Telefon. */
  schild?: string;
}) {
  const t = useT();
  const { ziel, beiWahl } = useContext(Ziel);
  const telefon = useTelefon();
  // Beim ersten Bild steht das Ziel noch nicht; ein Zustandswechsel holt
  // das Portal nach, sobald es da ist.
  const [bereit, setzeBereit] = useState(false);
  useEffect(() => setzeBereit(true), []);

  if (telefon) {
    return schirm
      ? <nav className="nebenliste" {...(schild ? { "aria-label": t(schild) } : {})}>{children}</nav>
      : null;
  }
  if (!ziel || !bereit) return null;
  // Der eigene Rahmen ist nicht Zierde: Auf dem Telefon liegt die Leiste
  // über der Maske, und wer dort ein Blatt wählt, will sie danach zu
  // haben. Die Punkte kommen per Portal aus einer anderen Stelle des
  // Baums — ein `onClick` an der Leiste selbst bekäme sie nie zu sehen,
  // weil React Ereignisse am BAUM entlangreicht, nicht am Dokument.
  return createPortal(<div onClick={beiWahl}>{children}</div>, ziel);
}
